import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateFareSummery } from "../../../redux/action/busAction";
import {
  Amount,
  BaseFare,
  DepartureAmmount,
  Discount,
  FareSummaryHeader,
  FareSummaryWrapper,
  SubTotalAmount,
  Summary,
  TotalAmount,
} from "./FareSummery.styles";

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const money = (value) => toNumber(value).toFixed(2);

const readStorage = (key, fallback = null) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    console.error(`Unable to read ${key} from localStorage:`, error);
    return fallback;
  }
};

const FareSummery = ({ discountValue, subTripid, setSubtripid }) => {
  const dispatch = useDispatch();

  const { webSettingData = {}, languageData = {} } = useSelector(
    (state) => state.busLists || {}
  );

  const [taxApi, setTaxApi] = useState({ data: [], tax_type: "" });
  const [bookingInfo, setBookingInfo] = useState(null);
  const [journeyInfo, setJourneyInfo] = useState(null);
  const [discount, setDiscount] = useState(null);

  const currency = webSettingData?.currency_code || "KES";
  const language = webSettingData?.language;

  const translate = useCallback(
    (key, fallback) => {
      const value = languageData?.[key];
      return value?.[language] || fallback;
    },
    [languageData, language]
  );

  // Load booking information and tax configuration.
  useEffect(() => {
    setBookingInfo(readStorage("bookingInfo"));
    setJourneyInfo(readStorage("journeyInfo"));

    let active = true;

    const loadTaxes = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_MODULE_DOMAIN}/taxs`
        );

        if (!response.ok) {
          throw new Error(`Tax request failed: ${response.status}`);
        }

        const data = await response.json();

        if (active) {
          setTaxApi({
            ...data,
            data: Array.isArray(data?.data) ? data.data : [],
          });
        }
      } catch (error) {
        console.error("Unable to load tax configuration:", error);
        if (active) {
          setTaxApi({ data: [], tax_type: "" });
        }
      }
    };

    loadTaxes();

    return () => {
      active = false;
    };
  }, []);

  // Refresh discount whenever the discount or selected subtrip changes.
  useEffect(() => {
    setDiscount(readStorage("discount"));
  }, [discountValue, subTripid]);

  // Keep the selected subtrip synchronized with localStorage.
  useEffect(() => {
    const storedSubtrip = readStorage("subtripId");
    if (storedSubtrip !== null && setSubtripid) {
      setSubtripid(storedSubtrip);
    }
  }, [setSubtripid]);

  const isRoundTrip = Boolean(journeyInfo?.isRoundTrip);
  const isExclusiveTax = taxApi?.tax_type === "exclusive";
  const taxes = Array.isArray(taxApi?.data) ? taxApi.data : [];

  const getTax = useCallback(
    (amount, luggageCost = 0) => {
      const taxableAmount = toNumber(amount) + toNumber(luggageCost);

      return taxes.reduce((total, item) => {
        return total + (toNumber(item?.value) * taxableAmount) / 100;
      }, 0);
    },
    [taxes]
  );

  const outboundFare = toNumber(
    isRoundTrip ? journeyInfo?.totalprice : bookingInfo?.totalprice
  );

  const returnFare = isRoundTrip
    ? toNumber(bookingInfo?.totalprice)
    : 0;

  const outboundLuggage = toNumber(
    isRoundTrip
      ? journeyInfo?.luggageInfo?.luggageCost
      : bookingInfo?.luggageInfo?.luggageCost
  );

  const returnLuggage = isRoundTrip
    ? toNumber(bookingInfo?.luggageInfo?.luggageCost)
    : 0;

  const outboundTax = getTax(outboundFare, outboundLuggage);
  const returnTax = isRoundTrip
    ? getTax(returnFare, returnLuggage)
    : 0;

  // Exclusive tax is added to the fare; inclusive tax is already included.
  const outboundBaseFare = isExclusiveTax
    ? outboundFare
    : Math.max(0, outboundFare - getTax(outboundFare, outboundLuggage));

  const returnBaseFare = isRoundTrip
    ? isExclusiveTax
      ? returnFare
      : Math.max(0, returnFare - getTax(returnFare, returnLuggage))
    : 0;

  const outboundTotal = outboundFare + outboundLuggage +
    (isExclusiveTax ? outboundTax : 0);

  const returnTotal = isRoundTrip
    ? returnFare + returnLuggage + (isExclusiveTax ? returnTax : 0)
    : 0;

  const subtotal = outboundTotal + returnTotal;

  const discountAmount = Math.min(
    subtotal,
    Math.max(0, toNumber(discount?.discount))
  );

  const grandTotal = Math.max(0, subtotal - discountAmount);

  // Update Redux with the calculated fare values.
  useEffect(() => {
    const fareSummary = {
      tax: outboundTax + returnTax,
      grandTotalOneWay: Math.max(
        0,
        outboundTotal - discountAmount
      ),
      grandTotalRound: Math.max(
        0,
        returnTotal - discountAmount
      ),
      grandTotal,
    };

    dispatch(updateFareSummery(fareSummary));
  }, [
    dispatch,
    outboundTax,
    returnTax,
    outboundTotal,
    returnTotal,
    discountAmount,
    grandTotal,
  ]);

  // Preserve the localStorage values used by the existing payment flow.
  useEffect(() => {
    if (!bookingInfo) return;

    const bookingTax = getTax(
      bookingInfo?.grandtotal,
      bookingInfo?.luggageInfo?.luggageCost
    );

    const journeyTax = getTax(
      journeyInfo?.grandtotal,
      journeyInfo?.luggageInfo?.luggageCost
    );

    localStorage.setItem("bookingInfoTax", JSON.stringify(bookingTax));
    localStorage.setItem("journeyInfoTax", JSON.stringify(journeyTax));

    const bookingGrandTotal = toNumber(bookingInfo?.grandtotal);
    const journeyGrandTotal = toNumber(journeyInfo?.grandtotal);

    let regularAmount = journeyGrandTotal;
    let returnAmount = bookingGrandTotal;

    if (isExclusiveTax) {
      if (isRoundTrip) {
        regularAmount += journeyTax;
        returnAmount += bookingTax + returnLuggage;
      } else {
        returnAmount =
          bookingGrandTotal -
          discountAmount +
          bookingTax +
          outboundLuggage;
      }
    }

    const storedDiscount = toNumber(discount?.discount);

    if (isRoundTrip) {
      if (subTripid === journeyInfo?.subtripId && storedDiscount > 0) {
        regularAmount = Math.max(0, regularAmount - storedDiscount);
      }

      if (subTripid === bookingInfo?.subtripId && storedDiscount > 0) {
        returnAmount = Math.max(0, returnAmount - storedDiscount);
      }
    } else if (
      subTripid === bookingInfo?.subtripId &&
      storedDiscount > 0 &&
      !isExclusiveTax
    ) {
      returnAmount = Math.max(0, returnAmount - storedDiscount);
    }

    localStorage.setItem("regular", JSON.stringify(regularAmount));
    localStorage.setItem("return", JSON.stringify(returnAmount));
  }, [
    bookingInfo,
    journeyInfo,
    getTax,
    isExclusiveTax,
    isRoundTrip,
    discount,
    discountAmount,
    subTripid,
    outboundLuggage,
    returnLuggage,
  ]);

  const passengerCount = (value) => {
    if (value === "" || value === null || value === undefined) return 0;
    return toNumber(value);
  };

  const PassengerRow = ({ data }) => (
    <div>
      {data.count} {data.label}
    </div>
  );

  const outboundPassengers = [
    {
      count: passengerCount(
        isRoundTrip ? journeyInfo?.aseat : bookingInfo?.aseat
      ),
      label: translate("adult", "Normal"),
    },
    {
      count: passengerCount(
        isRoundTrip ? journeyInfo?.cseat : bookingInfo?.cseat
      ),
      label: translate("child", "Business"),
    },
    {
      count: passengerCount(
        isRoundTrip ? journeyInfo?.spseat : bookingInfo?.spseat
      ),
      label: translate("special", "VIP"),
    },
  ];

  const returnPassengers = [
    {
      count: passengerCount(bookingInfo?.aseat),
      label: translate("adult", "Normal"),
    },
    {
      count: passengerCount(bookingInfo?.cseat),
      label: translate("children", "Business"),
    },
    {
      count: passengerCount(bookingInfo?.spseat),
      label: translate("special", "VIP"),
    },
  ];

  const FareRow = ({ label, value }) => (
    <BaseFare>
      <div>{label}</div>
      <strong>{currency} {money(value)}</strong>
    </BaseFare>
  );

  return (
    <Summary>
      <FareSummaryWrapper>
        <FareSummaryHeader>
          {translate("side_bar_fare_summery_title", "Fare Summary")}
        </FareSummaryHeader>

        <div
          style={{
            display: "flex",
            gap: "18px",
            margin: "6px 0 10px",
            fontSize: "12px",
            lineHeight: 1.5,
          }}
        >
          <div>
            {outboundPassengers.map((item, index) => (
              <PassengerRow key={`outbound-${index}`} data={item} />
            ))}
          </div>

          {isRoundTrip && (
            <div>
              {returnPassengers.map((item, index) => (
                <PassengerRow key={`return-${index}`} data={item} />
              ))}
            </div>
          )}
        </div>
      </FareSummaryWrapper>

      <Amount>
        <DepartureAmmount>
          <FareRow
            label={translate("side_bar_base_fare_title", "Base fare")}
            value={outboundBaseFare}
          />

          {webSettingData?.luggage_service && (
            <FareRow
              label={translate("luggage_cost", "Luggage cost")}
              value={outboundLuggage}
            />
          )}

          <FareRow
            label={translate("side_bar_tax_title", "Tax")}
            value={outboundTax}
          />
        </DepartureAmmount>

        {isRoundTrip && (
          <>
            <FareRow
              label={translate(
                "side_bar_return_ticket_fare_title",
                "Return ticket fare"
              )}
              value={returnBaseFare}
            />

            {webSettingData?.luggage_service && (
              <FareRow
                label={translate("luggage_cost_return", "Return luggage")
                }
                value={returnLuggage}
              />
            )}

            <FareRow
              label={translate(
                "side_bar_return_ticket_tax_title",
                "Return ticket tax"
              )}
              value={returnTax}
            />
          </>
        )}
      </Amount>

      <SubTotalAmount>
        <div>
          {translate("side_bar_sub_total_ammount_title", "Subtotal amount")}
        </div>
        <strong>{currency} {money(subtotal)}</strong>
      </SubTotalAmount>

      <Discount>
        <div>
          {translate("side_bar_total_discount", "Discount")}
        </div>
        <strong>{currency} {money(discountAmount)}</strong>
      </Discount>

      <TotalAmount>
        <div>
          {translate("side_bar_total_ammount", "Total amount")}
        </div>
        <strong>{currency} {money(grandTotal)}</strong>
      </TotalAmount>
    </Summary>
  );
};

export default FareSummery;
